<template>
  <div class="fixed inset-0 bg-black/50 flex justify-center items-center z-50">
    <div class="bg-white rounded-lg shadow-lg w-full max-w-6xl mx-auto">
      <div class="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
        <h2 class="text-xl font-semibold text-gray-800">JSON Editor</h2>
        <div class="flex gap-2">
          <button
            @click="toggleView"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            {{ viewMode === 'tree' ? 'Raw JSON' : 'Tree View' }}
          </button>
          <button
            v-if="viewMode === 'tree'"
            @click="addRootProperty"
            class="btn-primary-sm"
          >
            Add Property
          </button>
        </div>
      </div>

      <!-- Content -->
      <div class="p-6 max-h-[70vh] overflow-y-auto">
        <!-- Tree View -->
        <div v-if="viewMode === 'tree'" class="space-y-2">
          <div v-if="typeof jsonData === 'string'" class="text-red-500 text-center py-8">
            Error: Data is still a string after parsing. Check console for details.
          </div>
          <div v-else-if="!jsonData || typeof jsonData !== 'object'" class="text-red-500 text-center py-8">
            Error: Invalid data structure. Expected object or array.
          </div>
          <div v-else-if="Object.keys(jsonData).length === 0" class="text-gray-500 text-center py-8">
            No properties yet. Click "Add Property" to start.
          </div>
          <JSON_Node
            v-else
            v-for="(value, key) in jsonData"
            :key="key"
            :node-key="key"
            :node-value="value"
            :path="[key]"
            @update="updateValue"
            @delete="deleteProperty"
            @add="addProperty"
          />
        </div>

        <!-- Raw JSON View -->
        <div v-else class="space-y-4">
          <textarea
            v-model="rawJson"
            @input="validateJson"
            class="w-full h-96 p-4 font-mono text-sm border rounded-md focus:ring-2 focus:ring-blue-dark focus:border-blue-500"
            :class="jsonError ? 'border-red-500' : 'border-gray-300'"
            placeholder="Enter JSON here..."
          ></textarea>
          <div v-if="jsonError" class="text-red-600 text-sm">
            {{ jsonError }}
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div class="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
        <div class="text-sm text-gray-600">
          {{ jsonData && typeof jsonData === 'object' ? Object.keys(jsonData).length : 0 }} root properties
        </div>
        <div class="flex gap-2">
          <button
            @click="close"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors"
          >
            Close
          </button>
          <button
            @click="saveJson"
            class="px-4 py-2 text-sm font-medium text-white bg-green-600 hover:bg-green-700 rounded-md transition-colors"
          >
            Save
          </button>
        </div>
      </div>
    </div>
  </div>

</template>

<script>
import JSON_Node from "@/components/widgetcomponents/JSON_Editors/JSON_Node.vue";

export default {
  name: 'JsonEditor',
  components: {
    JSON_Node
  },
  props: {
    initialData: {
      type: [Object, Array, String],
      default: () => ({})
    }
  },
  data() {
    return {
      jsonData: {},
      viewMode: 'tree',
      rawJson: '{}',
      jsonError: ''
    };
  },
  mounted() {
    // Handle both JSON strings and objects
    let dataToUse = this.initialData;

    // If initialData is a string, try to parse it (possibly multiple times for double-encoded JSON)
    if (typeof this.initialData === 'string') {
      try {
        dataToUse = JSON.parse(this.initialData);

        // Check if the result is still a string (double-encoded JSON)
        if (typeof dataToUse === 'string') {
          console.log('Data appears to be double-encoded, parsing again...');
          dataToUse = JSON.parse(dataToUse);
        }
      } catch (e) {
        console.error('Failed to parse initial data as JSON:', e);
        console.error('Raw string:', this.initialData.substring(0, 200) + '...');
        dataToUse = {};
      }
    }

    // Ensure we have an object or array
    if (typeof dataToUse !== 'object' || dataToUse === null) {
      console.warn('Data is not an object or array after parsing, using empty object');
      dataToUse = {};
    }

    console.log('Final parsed data:', dataToUse);
    console.log('Number of root keys:', Object.keys(dataToUse).length);

    this.jsonData = JSON.parse(JSON.stringify(dataToUse));
    this.rawJson = JSON.stringify(this.jsonData, null, 2);
  },
  watch: {
    initialData: {
      handler(newData) {
        let dataToUse = newData;

        console.log('Initial data changed, type:', typeof newData);

        // If newData is a string, try to parse it (possibly multiple times for double-encoded JSON)
        if (typeof newData === 'string') {
          try {
            dataToUse = JSON.parse(newData);

            // Check if the result is still a string (double-encoded JSON)
            if (typeof dataToUse === 'string') {
              console.log('Data appears to be double-encoded, parsing again...');
              dataToUse = JSON.parse(dataToUse);
            }
          } catch (e) {
            console.error('Failed to parse data as JSON:', e);
            dataToUse = {};
          }
        }

        // Ensure we have an object or array
        if (typeof dataToUse !== 'object' || dataToUse === null) {
          console.warn('Data is not an object or array after parsing, using empty object');
          dataToUse = {};
        }

        this.jsonData = JSON.parse(JSON.stringify(dataToUse));
        this.rawJson = JSON.stringify(this.jsonData, null, 2);
      },
      deep: true
    },
    jsonData: {
      handler(newData) {
        if (this.viewMode === 'tree') {
          this.rawJson = JSON.stringify(newData, null, 2);
        }
        this.$emit('update', newData);
      },
      deep: true
    }
  },
  methods: {
    toggleView() {
      if (this.viewMode === 'tree') {
        this.rawJson = JSON.stringify(this.jsonData, null, 2);
      }
      this.viewMode = this.viewMode === 'tree' ? 'raw' : 'tree';
    },
    validateJson() {
      try {
        this.jsonData = JSON.parse(this.rawJson);
        this.jsonError = '';
      } catch (e) {
        this.jsonError = e.message;
      }
    },
    updateValue(path, value, newKey) {
      let current = this.jsonData;
      const keys = [...path];
      const lastKey = keys.pop();
      const parentKey = keys[keys.length - 1];

      for (const key of keys) {
        current = current[key];
      }

      if (newKey !== undefined && newKey !== lastKey) {
        const parent = keys.length === 0 ? this.jsonData : current;
        if (Array.isArray(parent)) {
          parent[newKey] = value;
        } else {
          delete parent[lastKey];
          parent[newKey] = value;
        }
      } else {
        current[lastKey] = value;
      }

      // Force reactivity update
      this.jsonData = { ...this.jsonData };
    },
    deleteProperty(path) {
      let current = this.jsonData;
      const keys = [...path];
      const lastKey = keys.pop();

      for (const key of keys) {
        current = current[key];
      }

      if (Array.isArray(current)) {
        current.splice(lastKey, 1);
      } else {
        delete current[lastKey];
      }

      // Force reactivity update
      this.jsonData = { ...this.jsonData };
    },
    addProperty(path, value) {
      let current = this.jsonData;
      const keys = [...path];
      const lastKey = keys.pop();

      for (const key of keys) {
        current = current[key];
      }

      if (Array.isArray(current)) {
        current.push(value);
      } else {
        current[lastKey] = value;
      }

      // Force reactivity update
      this.jsonData = { ...this.jsonData };
    },
    addRootProperty() {
      const key = prompt('Enter property name:');
      if (key) {
        this.jsonData[key] = '';
      }
    },
    close() {
      this.$emit('close')
    },
    saveJson() {
      this.$emit('save', this.jsonData);
    }
  }
};
</script>

<style scoped>
/* Add any additional component-specific styles here if needed */
</style>
